import * as React from 'react';

import { cn } from '@/shared/helpers';

const Card = React.forwardRef<
  HTMLElement,
  React.ComponentProps<'section'> & { size?: 'default' | 'sm' }
>(({ className, size = 'default', ...props }, ref) => (
  <section
    ref={ref}
    data-slot="card"
    data-size={size}
    className={cn(
      'group/card flex flex-col overflow-hidden rounded-xl border border-border bg-card text-sm text-card-foreground *:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl',
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'group/card-header @container/card-header grid gap-1 rounded-t-xl px-4 not-last:border-b group-data-[size=sm]/card:px-3',
        // default (no description): fixed height, vertically centered
        'h-13 grid-rows-[1fr] items-center group-data-[size=sm]/card:h-11',
        // with description: auto-sized 2-row grid, items-start, internal vertical padding
        'has-data-[slot=card-description]:h-auto has-data-[slot=card-description]:grid-rows-[auto_auto] has-data-[slot=card-description]:items-start has-data-[slot=card-description]:py-4 group-data-[size=sm]/card:has-data-[slot=card-description]:py-3',
        // action adds a second column in either mode
        'has-data-[slot=card-action]:grid-cols-[1fr_auto]',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('font-heading text-sm leading-snug font-semibold', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 justify-self-end', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('p-4 group-data-[size=sm]/card:p-3', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'flex items-center rounded-b-xl border-t bg-muted/50 p-4 group-data-[size=sm]/card:p-3 [[data-slot=card-header]+&]:border-t-0',
        className,
      )}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
